<template>
  <div class="full-page-width relative locations">
    <div class="row">
      <div class="col-xs-4 text-box">
        <InfoTextBox
          :title="$t('End of day')"
          :text="$t('eod-info-text')"
          :btnText="$t('Create EOD')"
          @action="createEOD" />
      </div>
      <div class="col-xs-8">
        <!-- Initial skeleton: shown when loading first page and no items yet -->
        <div v-if="loading && get_end_of_day_list.items.length === 0" class="list-table">
          <div class="table">
            <div class="table-body">
              <div
                class="table-row-holder"
                v-for="i in skeletonLoadingList"
                :key="`skeleton-${i}`">
                <TableRowSkeleton
                  :checkbox="false"
                  :type="true"
                  :data="skeletonReferenceData"/>
              </div>
            </div>
          </div>
        </div>

        <!-- Actual content when loaded -->
        <ul class="list-table" v-else-if="get_end_of_day_list.items.length > 0">
          <li
            v-for="(item, index) in get_end_of_day_list.items" :key="`item-${index}`"
            @mouseover="hoveredIndex = index"
            @mouseleave="hoveredIndex = null"
            class="list-table-item"
          >
            <div class="table-cell small">
              <div class="status-logo-box float-left">
                <Icon
                  v-if="item.batch_agents_array.length > 1"
                  :circle=true
                  name="fileCopies"
                  size="24" />
                <LogosSVG
                  v-if="item.batch_agents_array.length == 1"
                  :name="item.batch_agents_array[0]"
                  size="45" />
              </div>
            </div>
            <div class="table-cell">
              <p>
                <span class="main-info">
                  {{ `${item.colli_amount} ${$t('colli').toLowerCase()}` }}
                  {{ `(${parseFloat(item.batch_weight).toFixed(2)} kg.)` }}
                </span>
                <span class="additional-info">
                  {{ formatDate(item.batch_date) }}
                </span>
              </p>
            </div>
            <div
              v-if="hoveredIndex === index"
              style="position: absolute; top: 50%; right: 20px; transform: translateY(-50%); z-index: 10; display: flex; gap: 8px; align-items: center;"
            >
              <Btn
                class="brand"
                style="position: relative; display: block; transform: none;"
                :icon="{ name: 'preview', size: '16' }"
                :btnText="$t('view')"
                @btnClick="goToPage(item.batch_number)"/>
              <Btn
                class="brand"
                style="position: relative; display: block; transform: none;"
                :icon="{ name: 'cloudDownload', size: '16' }"
                :btnText="$t('Download EOD')"
                @btnClick="downloadEOD(item.batch_number)"/>
            </div>
          </li>
        </ul>

        <!-- Empty state -->
        <div class="table" v-else-if="!loading">
          <div class="table-body">
            <div class="table-row-holder" style="text-align: center; padding: 20px;">
              {{ $t('No results') }}
            </div>
          </div>
        </div>

        <!-- Hidden download anchor -->
        <a style="display: none" ref="downloadEODref" :href="eodPdfPath" download="end-of-day.pdf" />

        <!-- Infinite scroll sentinel -->
        <div v-if="hasMore && !loading" v-observe-visibility="visibilityOptions"></div>

        <!-- Load-more skeleton: appended below items while fetching next page -->
        <div v-if="loadingMore" class="list-table">
          <div class="table">
            <div class="table-body">
              <div
                class="table-row-holder"
                v-for="i in skeletonLoadingList"
                :key="`more-${i}`">
                <TableRowSkeleton
                  :checkbox="false"
                  :type="true"
                  :data="skeletonReferenceData"/>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
    <Popup
      v-if="openPopup"
      :popupSmall=true
      minHeight="450px"
      :confirmText="$t('generate')"
      @cancel="togglePopup"
      @confirm="submitForm">
      <template v-slot:header>
        <h2>{{ $t('Create EOD') }}</h2>
      </template>
      <div>
        <div class="col-xs-12">
          <InputSelect
            class="m-b-20"
            :multiSelect=true
            :label="$t('accounts')"
            @change="selectSubaccount"
            :preselected="subaccounts"
            :options="get_allowed_subaccounts_select_options"
            :rules="{required: true}" />
        </div>
        <div class="col-xs-6 m-b-20">
          <Calendar
            :disable-weekend="false"
            :noMinDate="true"
            :label="$t('from_date')"
            v-model="fromDate"
            :date="fromDate"
            :rules="{required: true}" />
        </div>
        <div class="col-xs-6 m-b-20">
          <Calendar
            :disable-weekend="false"
            :label="$t('to_date')"
            v-model="toDate"
            :date="toDate"
            :min-date-prop="fromDate"
            :rules="{required: true}" />
        </div>
        <div class="col-xs-12">
          <InputSelect
            class="m-b-20"
            :multiSelect=true
            :label="$t('shipping_agent_name')"
            :preselected="carriers"
            @change="selectAgents"
            :options="get_company_available_shipping_agents_select"
          />
        </div>
        <div class="col-xs-12">
          <country-select
            :multi-select="true"
            :noContinent="true"
            :preselected="countries"
            @change="selectCountries"
          />
        </div>
      </div>
    </Popup>
  </div>
</template>
<script>
/* eslint-disable */
import { mapGetters } from 'vuex';
import AddressPicker from "@/components/global/addressPicker/index.vue";
import PhoneSelect from "@/components/elements/inputs/phone.vue";
import CountryFlag from 'vue-country-flag';
import Btn from '@/components/elements/buttons';
import InputText from '@/components/elements/inputs/text.vue';
import Popup from '@/components/global/popup';
import InputSelect from '@/components/elements/inputs/select.vue';
import Toggle from '@/components/elements/inputs/toggle.vue';
import countryList from '@/data/country.js';
import LogosSVG from "@/assets/images/logosSVG";
import date from '@/mixins/date'
import Calendar from '@/components/elements/inputs/calendar.vue';
import InfoTextBox from '@/components/global/infoTextBox';
import CountrySelect from "../../components/elements/inputs/countrySelect.vue";
import TableRowSkeleton from '@/components/table/tableRowSkeleton.vue'; // Add this import

export default {
  name: 'EndOfDay',
  components: {
    CountrySelect,
    Btn,
    AddressPicker,
    PhoneSelect,
    Popup,
    InputText,
    InputSelect,
    Toggle,
    InfoTextBox,
    LogosSVG,
    Calendar,
    TableRowSkeleton, // Add this component
  },
  mixins: [date],
  data() {
    return {
      page: 1,
      loading: false, // Add loading state
      loadingMore: false,
      openPopup: false,
      isEdit: false,
      hoveredIndex: null,
      eodPdfPath: '',
      carriers: [],
      countries: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20", "21", "22", "23", "24", "25", "26", "27", "28", "29", "30", "31", "32", "33", "34", "35", "36", "37", "38", "39", "236", "40", "41", "42", "43", "44", "45", "46", "47", "48", "49", "50", "51", "52", "53", "54", "55", "56", "57", "58", "59", "60", "61", "62", "63", "64", "65", "66", "67", "68", "69", "70", "71", "72", "73", "74", "75", "76", "77", "78", "79", "80", "81", "82", "83", "84", "85", "86", "87", "88", "89", "90", "91", "235", "92", "93", "94", "95", "96", "97", "98", "99", "100", "101", "102", "103", "104", "105", "106", "107", "108", "109", "110", "111", "112", "113", "114", "115", "116", "117", "118", "119", "120", "121", "122", "123", "124", "125", "126", "127", "128", "129", "130", "131", "132", "133", "134", "135", "136", "137", "138", "139", "140", "141", "142", "143", "144", "145", "146", "147", "148", "149", "150", "151", "152", "153", "154", "155", "156", "157", "158", "159", "160", "161", "162", "163", "164", "165", "166", "167", "168", "169", "170", "171", "172", "173", "174", "175", "176", "177", "178", "179", "180", "181", "182", "183", "184", "185", "186", "187", "188", "189", "190", "191", "192", "193", "194", "195", "196", "197", "198", "199", "200", "201", "202", "203", "204", "205", "206", "207", "208", "209", "210", "211", "212", "213", "214", "215", "216", "217", "218", "219", "220", "221", "222", "223", "224", "225", "226", "227", "228", "229", "230", "231", "232", "233", "234", "237", "238", "239", "240", "241", "242", "243", "244", "245", "246", "247", "248", "249", "250", "251", "252", "253", "254", "255", "256", "257", "258", "259"],
      fromDate: new Date(),
      toDate: new Date(),
      subaccounts: []
    };
  },
  computed: {
    ...mapGetters([
      'get_end_of_day_list',
      'get_company_available_shipping_agents_select',
      'get_allowed_subaccounts_select_options'
    ]),
    // Add skeleton loading configuration
    skeletonLoadingList() {
      return Array.from({length: 5}, (_, i) => i + 1); // Show 5 skeleton rows
    },
    skeletonReferenceData() {
      return [
        {field: 'logo'},
        {field: 'info'}
      ];
    },
    hasMore() {
      return this.get_end_of_day_list.count > this.get_end_of_day_list.items.length;
    },
    visibilityOptions() {
      return {
        callback: this.visibilityChanged,
        intersection: {
          threshold: 0.1,
        },
      };
    },
  },
  created() {
    this.getShipments();
    this.setInitialSelectedSubaccounts()
  },
  methods: {
    selectSubaccount(val) {
      this.subaccounts = val;
    },
    setInitialSelectedSubaccounts() {
      this.subaccounts = this.get_allowed_subaccounts_select_options.map((obj) => {
        return obj.value;
      });
    },
    createEOD() {
      this.isEdit = false;
      this.togglePopup();
      this.$store.dispatch('GET_COMPANY_AVAILABLE_SHIPPING_AGENTS').then((res) => {
        this.carriers = res.map((agent) => {
          return String(agent.id)
        });
      });
    },
    togglePopup() {
      this.openPopup = !this.openPopup;
      this.fromDate = '';
      this.toDate = '';
    },
    selectAgents(val) {
      this.carriers = val;
    },
    selectCountries(val) {
      this.countries = val;
    },
    submitForm() {
      this.loading = true; // Show loading when creating EOD
      this.$store.dispatch('SET_EOD_SHIPMENTS', {
        fromDate: this.fromDate,
        toDate: this.toDate,
        carriers: JSON.stringify(this.carriers),
        subaccounts: JSON.stringify(this.subaccounts),
        countries: JSON.stringify(this.countries)
      }).then((res) => {
        this.togglePopup();
        this.page = 1;
        this.getShipments();
      }).finally(() => {
        this.loading = false; // Hide loading when done
      });
    },
    getShipments(isLoadMore = false) {
      if (isLoadMore) {
        this.loadingMore = true;
      } else {
        this.loading = true;
      }
      this.$store.dispatch('GET_SHIPMENTS_EOD', this.page).finally(() => {
        this.loading = false;
        this.loadingMore = false;
      });
    },
    goToPage(pageID) {
      this.$router.push(`end-of-day/${pageID}`);
    },
    downloadEOD(batchId) {
      this.$store.dispatch('DOWNLOAD_EOD', batchId).then((res) => {
        this.$nextTick(() => {
          let file = window.URL.createObjectURL(res);
          this.$refs.downloadEODref.href = file;
          this.eodPdfPath = file;
          this.$refs.downloadEODref.click();
          window.URL.revokeObjectURL(file);
        });
      });
    },
    visibilityChanged(isVisible) {
      if (!isVisible || this.loadingMore || this.loading || !this.hasMore) return;
      this.loadingMore = true;
      this.page++;
      this.getShipments(true);
    },
  },
};
</script>
